import { apiServiceUrl } from '../constants';
import { getLoggedInUser, emailFromUser } from './authUtils';

// Master switch for the public profile-sharing feature (share button/modal,
// inline share card, social deep-links, QR, and the /profile/shared/:shareKey
// landing page). While false the whole surface runs in "demo only" mode:
// in-app share controls are visible but inert, and the shared-link page shows
// only DEMO_ONLY_MESSAGE. Flip to true to fully restore sharing. A future
// configuration control center can source this value at runtime — every
// consumer reads it from here, so no call sites change when it does.
export const PROFILE_SHARING_ENABLED = false;
export const DEMO_ONLY_MESSAGE = 'This feature is for demonstration purpose only';

// Demo accounts get the full sharing surface while the master switch is off, so
// the client can demonstrate the feature end-to-end. Viewer-based: keys off who
// is logged in, not whose profile is displayed. Remove entries (or flip the
// master switch) when sharing launches for everyone.
const PROFILE_SHARING_DEMO_EMAILS = ['executive@prove-it-to.me'];

export const isProfileSharingEnabledForViewer = ({
  user = getLoggedInUser(),
  profileInformation = null,
  masterEnabled = PROFILE_SHARING_ENABLED,
} = {}) => {
  if (masterEnabled) return true;
  const email = emailFromUser(user, profileInformation).toLowerCase();
  return PROFILE_SHARING_DEMO_EMAILS.includes(email);
};

export const buildProfileSharePath = (shareKey) => `/profile/shared/${shareKey}`;

export const buildProfileShareUrl = (shareKey, origin) =>
  `${origin ?? window.location.origin}${buildProfileSharePath(shareKey)}`;

export const buildProfileQrUrl = (shareKey, apiBase = apiServiceUrl) =>
  `${apiBase}/ValueProposition/GetProfileQrCode?shareKey=${shareKey}`;

export const buildProfileAuthRedirect = (shareKey, basePath) =>
  `${basePath}?redirectUrl=${buildProfileSharePath(shareKey)}`;

const isBlank = (s) => s == null || String(s).trim() === '';

// Owner blocks arrive camelCase from the API but tolerate PascalCase too.
const readOwner = (owner, camel, pascal) => owner?.[camel] ?? owner?.[pascal];

export const resolveProfileDisplayName = (owner, fallback = 'Unnamed profile') => {
  const name = readOwner(owner, 'ownerName', 'OwnerName');
  return isBlank(name) ? fallback : String(name).trim();
};

export const resolveProfileInitials = (owner) => {
  const name = readOwner(owner, 'ownerName', 'OwnerName');
  if (isBlank(name)) return '?';
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

export const resolveProfileLocationLabel = (owner) =>
  [
    readOwner(owner, 'city', 'City'),
    readOwner(owner, 'stateProvince', 'StateProvince'),
    readOwner(owner, 'country', 'Country'),
  ]
    .filter((s) => !isBlank(s))
    .map((s) => String(s).trim())
    .join(', ');

// Prefer the VPX owner block avatar; fall back to saved personal-info logo when the
// cached VPX profile is stale (e.g. photo saved after the profile was loaded).
export const resolveOwnerAvatarURL = (owner, fallbackLogo = null) => {
  const fromOwner = readOwner(owner, 'ownerAvatarURL', 'OwnerAvatarURL');
  if (!isBlank(fromOwner)) return String(fromOwner).trim();
  if (fallbackLogo instanceof File) return null;
  if (!isBlank(fallbackLogo)) return String(fallbackLogo).trim();
  return null;
};

// Drives the "Complete your profile" meter. Every item is computed purely from
// the owner block already in the ViewUserProfile response plus the VP count.
export const computeProfileCompleteness = (owner, vpCount = 0, avatarFallback = null) => {
  const hasPhoto = !isBlank(resolveOwnerAvatarURL(owner, avatarFallback));
  const items = [
    { key: 'name', label: 'Add your name', done: !isBlank(readOwner(owner, 'ownerName', 'OwnerName')) },
    { key: 'photo', label: 'Add a profile photo', done: hasPhoto },
    { key: 'location', label: 'Add your location', done: !isBlank(resolveProfileLocationLabel(owner)) },
    { key: 'vp', label: 'Publish a value proposition', done: Number(vpCount) > 0 },
  ];
  const done = items.filter((item) => item.done).length;
  return {
    items,
    done,
    total: items.length,
    percent: Math.round((done / items.length) * 100),
    complete: done === items.length,
  };
};

// Own profile keeps "my"; sharing someone else's profile uses "this" so the
// message does not imply the sharer owns the VPX profile being linked.
export const OWN_PROFILE_SHARE_TEXT =
  'Check out my PROVE IT TO ME™ - Value Proposition Exchange (VPX) profile';
export const OTHER_PROFILE_SHARE_TEXT =
  'Check out this PROVE IT TO ME™ - Value Proposition Exchange (VPX) profile';

export const buildProfileShareText = (isOwnProfile = true) =>
  isOwnProfile ? OWN_PROFILE_SHARE_TEXT : OTHER_PROFILE_SHARE_TEXT;

export const PROFILE_SHARE_EMAIL_SUBJECT =
  'My PROVE IT TO ME™ - Value Proposition Exchange (VPX) profile';

export const buildSocialShareLinks = (
  shareUrl,
  text = OWN_PROFILE_SHARE_TEXT,
  emailSubject = PROFILE_SHARE_EMAIL_SUBJECT
) => {
  const url = encodeURIComponent(shareUrl ?? '');
  const message = encodeURIComponent(text);
  const messageWithUrl = encodeURIComponent(`${text} ${shareUrl ?? ''}`.trim());
  return {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    x: `https://twitter.com/intent/tweet?url=${url}&text=${message}`,
    whatsapp: `https://wa.me/?text=${messageWithUrl}`,
    email: `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${messageWithUrl}`,
  };
};
